import { Divider, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { Loading, PageHeader } from "components";
import PageContent from "components/layouts/PageContent";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomText from "components/ui/CustomText/CustomText";
import { RECEIPT_STATUS_COLOR, RECEIPT_STATUS_LABEL } from "constants/common/receipt";
import {
  SALES_ORDER_STATUS_COLOR,
  SALES_ORDER_STATUS_LABEL,
} from "constants/common/salesOrder";
import { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { DEBT_PAGE } from "routes/route.constant";
import { formatCurrency, formatDate } from "utils/helpers/format";
import { useReceiptDetail } from "utils/hooks/request/useReceipt";

const RECEIPT_PAGE_TITLE = "Phiếu thu công nợ";

// ─── sub-components ─────────────────────────────────────────────────────────

const InfoField = ({ label, value }: { label: string; value: ReactNode }) => (
  <Stack gap={2}>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text size="sm" fw={500}>
      {value ?? "—"}
    </Text>
  </Stack>
);

const SectionTitle = ({ children }: { children: string }) => (
  <CustomText variant="section-title">{children}</CustomText>
);

// ─── page ────────────────────────────────────────────────────────────────────

const DetailDebtReceipt = () => {
  const { id } = useParams<{ id: string }>();
  const { receipt, isLoading } = useReceiptDetail(id);

  return (
    <Stack gap="sm">
      <PageHeader
        breadcrumbs={[
          { title: "Công nợ", href: DEBT_PAGE },
          { title: RECEIPT_PAGE_TITLE, href: "" },
        ]}
      />

      <PageContent>
        <Loading variant="overlay" visible={isLoading}>
          {receipt && (
            <Stack gap="md">
              {/* ── Thông tin phiếu thu ── */}
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <SectionTitle>Thông tin phiếu thu</SectionTitle>

                  <SimpleGrid cols={{ base: 2, md: 3 }}>
                    <InfoField label="Mã phiếu thu" value={receipt.code} />
                    <InfoField
                      label="Trạng thái"
                      value={
                        <CustomBadge
                          label={RECEIPT_STATUS_LABEL[receipt.status] ?? receipt.status}
                          color={RECEIPT_STATUS_COLOR[receipt.status] ?? "gray"}
                        />
                      }
                    />
                    <InfoField
                      label="Ngày thu"
                      value={formatDate(receipt.paymentDate)}
                    />
                    <InfoField
                      label="Số tiền"
                      value={
                        <Text size="sm" fw={700} c="blue">
                          {formatCurrency(receipt.amount)}
                        </Text>
                      }
                    />
                    <InfoField
                      label="Người tạo"
                      value={receipt.createdBy.fullname}
                    />
                    <InfoField
                      label="Ngày tạo"
                      value={formatDate(receipt.createdAt)}
                    />
                    <InfoField
                      label="Ngày cập nhật"
                      value={formatDate(receipt.updatedAt)}
                    />
                  </SimpleGrid>

                  {receipt.notes && (
                    <>
                      <Divider />
                      <InfoField label="Ghi chú" value={receipt.notes} />
                    </>
                  )}
                </Stack>
              </Paper>

              {/* ── Thông tin đơn bán hàng ── */}
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <SectionTitle>Thông tin đơn bán hàng</SectionTitle>

                  <SimpleGrid cols={{ base: 2, md: 3 }}>
                    <InfoField
                      label="Mã đơn hàng"
                      value={receipt.saleOrder.code}
                    />
                    <InfoField
                      label="Trạng thái"
                      value={
                        <CustomBadge
                          label={
                            SALES_ORDER_STATUS_LABEL[receipt.saleOrder.status] ??
                            receipt.saleOrder.status
                          }
                          color={
                            SALES_ORDER_STATUS_COLOR[receipt.saleOrder.status] ??
                            "gray"
                          }
                        />
                      }
                    />
                    <InfoField
                      label="Ngày xác nhận"
                      value={formatDate(receipt.saleOrder.confirmedAt)}
                    />
                    <InfoField
                      label="Tổng tiền"
                      value={formatCurrency(receipt.saleOrder.totalAmount)}
                    />
                    <InfoField
                      label="Đã thanh toán"
                      value={formatCurrency(receipt.saleOrder.paidAmount)}
                    />
                  </SimpleGrid>

                  {receipt.saleOrder.notes && (
                    <>
                      <Divider />
                      <InfoField label="Ghi chú" value={receipt.saleOrder.notes} />
                    </>
                  )}
                </Stack>
              </Paper>

              {/* ── Phương thức thanh toán ── */}
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <SectionTitle>Phương thức thanh toán</SectionTitle>

                  {receipt.bankAccount ? (
                    <SimpleGrid cols={{ base: 2, md: 4 }}>
                      <InfoField
                        label="Mã tài khoản"
                        value={receipt.bankAccount.code}
                      />
                      <InfoField
                        label="Ngân hàng"
                        value={receipt.bankAccount.bankName}
                      />
                      <InfoField
                        label="Số tài khoản"
                        value={receipt.bankAccount.accountNumber}
                      />
                      <InfoField
                        label="Chủ tài khoản"
                        value={receipt.bankAccount.accountHolder}
                      />
                    </SimpleGrid>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Khách hàng trả bằng tiền mặt
                    </Text>
                  )}
                </Stack>
              </Paper>

              {/* ── Xét duyệt ── */}
              {receipt.reviewedAt && (
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <SectionTitle>Thông tin xét duyệt</SectionTitle>

                    <SimpleGrid cols={{ base: 2, md: 3 }}>
                      <InfoField
                        label="Người xét duyệt"
                        value={receipt.reviewedBy?.fullname}
                      />
                      <InfoField
                        label="Ngày xét duyệt"
                        value={formatDate(receipt.reviewedAt)}
                      />
                    </SimpleGrid>

                    {receipt.reviewNote && (
                      <>
                        <Divider />
                        <InfoField
                          label="Ghi chú xét duyệt"
                          value={receipt.reviewNote}
                        />
                      </>
                    )}
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}
        </Loading>
      </PageContent>
    </Stack>
  );
};

export default DetailDebtReceipt;
